import { useEffect, useMemo, useState } from 'react';

import CollectionsIcon from '@mui/icons-material/Collections';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    List,
    Stack,
} from '@mui/material';

import { Typography } from 'components/atoms/Typography';
import { useGetImages } from 'hooks/api/builder/images/useGetImages';
import { imageFileStorage } from 'types/api/imageFileStorage';

import { ImageDeleteModal } from './ImageDeleteModal';
import { ImageEditControl } from './ImageEditControl';
import { ImageListItem } from './ImageListItem';
import { ImageUploadControl } from './ImageUploadControl';

type ImageSelectorProps = {
    onSelect?: (image: imageFileStorage) => void;
    defaultSelectedUuid?: string;
};

export const ImageSelector = ({
    onSelect = () => undefined,
    defaultSelectedUuid,
}: ImageSelectorProps): JSX.Element => {
    const [open, setOpen] = useState(false);

    const [mode, setMode] = useState<'view' | 'edit' | 'add'>('view');

    const { images, loading, getAllImages } = useGetImages();

    const defaultImage = useMemo(
        () => images?.find((el) => el.uuid === defaultSelectedUuid),
        [defaultSelectedUuid, images]
    );

    const [selectedImage, setSelectedImage] = useState<
        imageFileStorage | undefined
    >(defaultImage);

    useEffect(() => {
        if (defaultImage) {
            setSelectedImage(defaultImage);
        }
    }, [defaultImage]);

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    return (
        <>
            <Button
                variant="outlined"
                onClick={() => setOpen(true)}
                endIcon={<CollectionsIcon />}
            >
                {defaultSelectedUuid
                    ? 'Заменить изображение'
                    : 'Выбрать изображение'}
            </Button>
            {defaultSelectedUuid && !defaultImage ? (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ color: 'error.main', mt: 0.5 }}
                >
                    <ReportProblemOutlinedIcon fontSize="small" />
                    <Typography
                        variant="body.small"
                        style={{ color: 'inherit' }}
                    >
                        Выбранное изображение было удалено
                    </Typography>
                </Stack>
            ) : undefined}
            {selectedImage ? (
                <ImageDeleteModal
                    id={selectedImage.id}
                    onClose={() => setDeleteModalOpen(false)}
                    onSuccess={() => {
                        setSelectedImage(undefined);
                        getAllImages();
                    }}
                    open={deleteModalOpen}
                    imageTitle={selectedImage.name}
                />
            ) : undefined}
            <Dialog
                open={open}
                PaperProps={{
                    sx: {
                        maxWidth: 800,
                    },
                }}
            >
                <DialogContent
                    sx={{
                        minWidth: 800,
                        minHeight: 500,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {mode === 'add' ? (
                        <ImageUploadControl
                            onClose={() => {
                                setMode('view');
                            }}
                            onSuccess={(image) => {
                                setMode('view');
                                setSelectedImage(image);
                                getAllImages();
                            }}
                        />
                    ) : undefined}
                    {mode === 'edit' && selectedImage ? (
                        <ImageEditControl
                            onClose={() => {
                                setMode('view');
                            }}
                            onSuccess={(image) => {
                                setMode('view');
                                setSelectedImage(image);
                                getAllImages();
                            }}
                            selectedImage={selectedImage}
                        />
                    ) : undefined}
                    {mode === 'view' ? (
                        <>
                            <Stack sx={{ flex: 1, overflow: 'auto' }}>
                                {!loading && images?.length ? (
                                    <List>
                                        {images.map((el) => (
                                            <ImageListItem
                                                key={el.id}
                                                item={el}
                                                onRequestEdit={() => {
                                                    setSelectedImage(el);
                                                    setMode('edit');
                                                }}
                                                onRequestDelete={() => {
                                                    setSelectedImage(el);
                                                    setDeleteModalOpen(true);
                                                }}
                                                onSelect={() =>
                                                    setSelectedImage(el)
                                                }
                                                selected={
                                                    selectedImage?.id === el.id
                                                }
                                            />
                                        ))}
                                    </List>
                                ) : undefined}

                                {loading ? (
                                    <Stack
                                        justifyContent="center"
                                        alignItems="center"
                                        sx={{ flex: 1 }}
                                    >
                                        <CircularProgress
                                            size={20}
                                            variant="indeterminate"
                                        />
                                    </Stack>
                                ) : undefined}
                                {!loading && !images?.length ? (
                                    <Stack
                                        justifyContent="center"
                                        alignItems="center"
                                        sx={{ flex: 1 }}
                                    >
                                        <Typography>
                                            Изображения отсутствуют
                                        </Typography>
                                    </Stack>
                                ) : undefined}
                            </Stack>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Button
                                    variant="text"
                                    onClick={() => setOpen(false)}
                                >
                                    Отмена
                                </Button>
                                <Button
                                    variant="outlined"
                                    sx={{ ml: 'auto', mr: 1 }}
                                    onClick={() => setMode('add')}
                                >
                                    Добавить новое
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={!selectedImage}
                                    onClick={() => {
                                        if (selectedImage) {
                                            onSelect(selectedImage);
                                        }
                                        setOpen(false);
                                    }}
                                >
                                    Выбрать
                                </Button>
                            </Stack>
                        </>
                    ) : undefined}
                </DialogContent>
            </Dialog>
        </>
    );
};
